import React, { useEffect, useState } from "react";
import ZoomableImage from "components/ZoomableImage";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { rotateVijilizeImage } from "services/project";
import { toast } from "react-toastify";

interface ExpandableZoomableImageProps {
  url: string;
  alt?: string;
  /** Viewport height of the inline (non-expanded) image. */
  height?: string;
  /** Header text of the expanded overlay (falls back to alt). */
  title?: string;
  /**
   * Source-document hyperlink shown beside the overlay title (patent /
   * target document, whatever its format).
   */
  link?: string;
  /** Text of the hyperlink, e.g. "View Patent" / "View Target". */
  linkLabel?: string;
  /**
   * Presigned link to the saved PDF snapshot of a website target — rendered
   * as a small pill button beside the primary link.
   */
  snapshotLink?: string;
  snapshotLabel?: string;
  /**
   * Figure legend rows ({ "110": "digital scanner" }) — shown beside the
   * image in the EXPANDED overlay only; the inline presentation is the
   * parent's concern. Rotation only affects the image itself.
   */
  legend?: Array<Record<string, string>>;
  /**
   * Called after a successful rotate-and-save with the fresh presigned URL,
   * so parents can sync every other surface showing the same image.
   */
  onRotated?: (newUrl: string, oldUrl: string) => void;
  /**
   * When set, the expanded overlay shows an "Annotation" button (target
   * images) that closes the overlay and opens the annotation editor.
   */
  onAnnotate?: () => void;
}

/**
 * ZoomableImage with an expand button (arrows pointing both ends, same icon as
 * the per-feature column view). Expanding opens a large centered overlay where
 * the same pan/pinch-zoom applies; close via the restore icon or a backdrop
 * click. A rotate button turns the image 90° clockwise and persists it to S3
 * (the object is overwritten in place, so all references stay valid).
 */
const ExpandableZoomableImage: React.FC<ExpandableZoomableImageProps> = ({
  url,
  alt = "",
  height,
  title,
  link,
  linkLabel = "View Source",
  snapshotLink,
  snapshotLabel = "View Snapshot",
  legend,
  onRotated,
  onAnnotate,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [rotating, setRotating] = useState(false);
  // Fallback for parents that don't sync state via onRotated: show the
  // rotated image locally. Reset whenever the parent hands us a new URL.
  const [overrideUrl, setOverrideUrl] = useState<string | null>(null);
  useEffect(() => setOverrideUrl(null), [url]);

  const effectiveUrl = overrideUrl || url;

  const handleRotate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (rotating || !effectiveUrl) return;
    setRotating(true);
    try {
      const response: any = await rotateVijilizeImage({
        image_url: effectiveUrl,
        degrees: 90,
      });
      const newUrl = response?.data?.url;
      if (!newUrl) throw new Error("No URL returned");
      setOverrideUrl(newUrl);
      onRotated?.(newUrl, effectiveUrl);
    } catch (error) {
      console.error("Error rotating image:", error);
      toast.error("Failed to rotate image. Please try again.");
    } finally {
      setRotating(false);
    }
  };

  if (!url) return null;

  const rotateBtnCls =
    "p-1 rounded bg-white/90 border border-gray-200 shadow-sm hover:bg-gray-100 text-[#667085] disabled:opacity-60";

  const legendList = (listCls: string, itemCls: string) =>
    legend && legend.length > 0 ? (
      <ul className={listCls}>
        {legend.map((lg, i) => {
          const k = Object.keys(lg || {})[0];
          if (!k) return null;
          return (
            <li key={i} className={itemCls}>
              {k}. {lg[k]}
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <div className="relative">
      <ZoomableImage url={effectiveUrl} alt={alt} height={height} />
      <div className="absolute top-1.5 right-1.5 z-10 flex flex-col gap-1">
        <button
          type="button"
          title="Expand"
          onClick={() => setExpanded(true)}
          className={rotateBtnCls}
          style={{ cursor: "pointer" }}
        >
          <OpenInFullIcon sx={{ fontSize: 15 }} />
        </button>
        <button
          type="button"
          title="Rotate 90° clockwise (saved)"
          onClick={handleRotate}
          disabled={rotating}
          className={rotateBtnCls}
          style={{ cursor: rotating ? "wait" : "pointer" }}
        >
          <RotateRightIcon
            sx={{ fontSize: 15 }}
            className={rotating ? "animate-spin" : ""}
          />
        </button>
      </div>

      {expanded && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 1200 }}
          onClick={() => setExpanded(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border-2 border-gray-200"
            style={{ width: "85vw", height: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
              <span className="flex items-center gap-3 min-w-0">
                <span className="text-[14px] font-interSemiBold text-[#1A202C] truncate">
                  {title || alt}
                </span>
                {link && (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[12px] text-[#1751D0] font-interMedium hover:underline flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {linkLabel}
                    <OpenInNewIcon sx={{ fontSize: 13 }} />
                  </a>
                )}
                {snapshotLink && (
                  <a
                    href={snapshotLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open the saved PDF snapshot of this website"
                    className="flex items-center gap-1 text-[11px] text-[#1751D0] font-interMedium border border-[#C9D9F5] bg-[#F5F8FD] rounded-full px-2.5 py-0.5 hover:bg-[#EAF1FB] flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PictureAsPdfOutlinedIcon sx={{ fontSize: 13 }} />
                    {snapshotLabel}
                  </a>
                )}
              </span>
              <span className="flex items-center gap-1">
                {onAnnotate && (
                  <button
                    type="button"
                    title="Open the annotation editor for this image"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(false);
                      onAnnotate();
                    }}
                    className="flex items-center gap-1 px-3 py-1 mr-1 text-xs text-white rounded flex-shrink-0"
                    style={{ backgroundColor: "#1751D0", border: "none", cursor: "pointer" }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    Annotation
                  </button>
                )}
                <button
                  type="button"
                  title="Rotate 90° clockwise (saved)"
                  onClick={handleRotate}
                  disabled={rotating}
                  className="p-1 rounded hover:bg-gray-100 text-[#667085] disabled:opacity-60"
                  style={{ cursor: rotating ? "wait" : "pointer" }}
                >
                  <RotateRightIcon
                    sx={{ fontSize: 17 }}
                    className={rotating ? "animate-spin" : ""}
                  />
                </button>
                <button
                  type="button"
                  title="Restore"
                  onClick={() => setExpanded(false)}
                  className="p-1 rounded hover:bg-gray-100 text-[#667085]"
                  style={{ cursor: "pointer" }}
                >
                  <CloseFullscreenIcon sx={{ fontSize: 17 }} />
                </button>
              </span>
            </div>
            <div className="flex-1 p-3 min-h-0 flex gap-4">
              <div className="flex-1 min-w-0">
                <ZoomableImage url={effectiveUrl} alt={alt} height="100%" />
              </div>
              {legendList(
                "w-64 flex-shrink-0 overflow-y-auto space-y-1.5 pl-4 border-l border-dashed border-gray-200 text-left [scrollbar-width:thin]",
                "text-[13px] leading-snug text-gray-800"
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpandableZoomableImage;
